/**
 * Lazy loader for the form-field renderer chunk (forms.js) — the
 * daily-loader pattern: the import URL is computed at runtime from the
 * widget script's own directory so esbuild leaves the `import()`
 * native, and the chunk loads only when a form-driven card actually
 * renders. Callers fall back to the legacy quartet card if the chunk
 * fails (transient network) — capture must never dead-end.
 */

import type {
  buildFormFields,
  parseFormFields,
  splitSubmission,
  validateFields,
} from './form-fields';

export type FormsModule = {
  buildFormFields: typeof buildFormFields;
  parseFormFields: typeof parseFormFields;
  splitSubmission: typeof splitSubmission;
  validateFields: typeof validateFields;
};

let chunkBase: string | null = null;
let cached: Promise<FormsModule> | null = null;

/** Set once at boot from the app.js directory (same base as daily/tours). */
export function setFormsChunkBase(base: string): void {
  chunkBase = base.replace(/\/$/, '');
}

export function loadForms(): Promise<FormsModule> {
  if (!cached) {
    if (!chunkBase) {
      return Promise.reject(
        new Error('[showpilot] forms chunk base not configured before form render'),
      );
    }
    const url = `${chunkBase}/forms.js`;
    cached = import(/* @vite-ignore */ url)
      .then((m: Partial<FormsModule>) => {
        if (typeof m.buildFormFields !== 'function' || typeof m.parseFormFields !== 'function') {
          throw new Error('[showpilot] forms chunk loaded but module shape is wrong');
        }
        return m as FormsModule;
      })
      .catch((err: unknown) => {
        cached = null; // let a later card retry after a transient failure
        throw err;
      });
  }
  return cached;
}

/** Test hook — reset module state between cases. */
export function resetFormsLoaderForTests(): void {
  chunkBase = null;
  cached = null;
}
